// src/pages/Page/Page.tsx
import React, { useState, Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import cx from "classnames";
import styles from "./styles.module.sass";

import Logo from "../../assets/images/logo.svg";
import LogoCanopy from "./logo-canopy.png";
import LogoBistro from "./logo-opeum.png";

import HorizontalNav from "../../components/HorizontalNav";
import Footer from "../../components/Footer";
import Drawer from "../../components/Drawer";
import GlobalNav from "../../components/GlobalNav";
import BookingWidget from "../../components/BookingWidget";
import Button from "../../components/Button";
import SocialIcons from "../../components/SocialIcons";
import { deslugify } from "../../helpers";

interface PageProps {
  children?: React.ReactNode;
  type?: "" | "local";
  country?: string;
  className?: string;
}

const PUBLIC_URL = process.env.PUBLIC_URL ?? "";

const Page: React.FC<PageProps> = ({
  type = "",
  country,
  className = "",
  children,
}) => {
  const [menuActive, setMenuActive] = useState(false);
  const [bookingActive, setBookingActive] = useState(false);
  const location = useLocation();
  const path = location.pathname.replace(PUBLIC_URL, "");

  const toggleMenu = () => {
    setMenuActive(true);
    setBookingActive(false);
  };

  const toggleBooking = () => {
    setBookingActive(true);
    setMenuActive(false);
  };

  const closeDrawer = (drawer?: "menu" | "booking") => {
    if (drawer === "menu") setMenuActive(false);
    else if (drawer === "booking") setBookingActive(false);
    else {
      setMenuActive(false);
      setBookingActive(false);
    }
  };

  const leftRight = cx({
    [styles.pushLeft]: menuActive,
    [styles.pushRight]: bookingActive,
  });

  // Home page layout
  if (path === "/" || path === "") {
    return (
      <div style={{ height: "100%" }}>
        <div className={cx(styles.home, leftRight)}>{children}</div>
        <div className={styles.navWrapper}>
          <div className={styles.nav}>
            <div onClick={toggleMenu} className={styles.menu__toggle} />
            <Button
              clickHandler={toggleBooking}
              buttonVariant="contrast"
              className={styles.book__btn}
            >
              Book <br className={styles.m} /> now
            </Button>
          </div>
        </div>
        <Drawer
          flush="left"
          active={menuActive}
          closeHandler={() => closeDrawer("menu")}
        >
          <GlobalNav />
        </Drawer>
        <Drawer
          flush="right"
          maxWidth="50rem"
          active={bookingActive}
          closeHandler={() => closeDrawer("booking")}
        >
          <BookingWidget city={country} />
        </Drawer>
      </div>
    );
  }

  // Inner pages
  return (
    <div className={styles.base}>
      <header className={cx(styles.header, leftRight)}>
        {path.includes("opeum-bistro") && (
          <img className={styles.logoPng} src={LogoBistro} alt="Opeum Bistro" />
        )}
        {path.includes("canopy-spa") && (
          <img
            className={styles.logoPng}
            src={LogoCanopy}
            alt="The Canopy Spa"
          />
        )}
        {!path.includes("canopy-spa") && !path.includes("opeum-bistro") && (
          <Fragment>
            <Link to={`${PUBLIC_URL}/`}>
              {country && (
                <div className={styles.logo}>
                  <Logo className={styles.logoImage} />
                  <div className={styles.country}>{deslugify(country)}</div>
                </div>
              )}
            </Link>
            <HorizontalNav type={type} />
          </Fragment>
        )}
      </header>

      <div className={cx(styles.content, leftRight, className)}>{children}</div>

      <Footer className={cx(leftRight)} />

      <div className={styles.navWrapper}>
        <div className={cx(styles.nav, leftRight)}>
          <div onClick={toggleMenu} className={styles.menu__toggle} />
          <Button
            clickHandler={toggleBooking}
            buttonVariant="contrast"
            className={styles.book__btn}
          >
            Book <br className={styles.m} /> now
          </Button>
        </div>
      </div>

      <Drawer
        flush="left"
        maxWidth="35rem"
        active={menuActive}
        closeHandler={() => closeDrawer("menu")}
      >
        <GlobalNav />
      </Drawer>
      <Drawer
        flush="right"
        maxWidth="50rem"
        active={bookingActive}
        closeHandler={() => closeDrawer("booking")}
      >
        <BookingWidget city={country} />
      </Drawer>

      {type === "local" && (
        <SocialIcons
          className={styles.socialSticky}
          containerClassName={styles.d}
        />
      )}
    </div>
  );
};

export default Page;
